import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";

import { toast } from "../hooks/use-toast";
import { hrAPI } from "../services/api";

import {
  Users,
  LogOut,
  Loader2,
  Search
} from "lucide-react";

const HRDashboard = () => {

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applications, setApplications] = useState([]);
  const [selectedStage, setSelectedStage] = useState("all");

  const [loadingApplications, setLoadingApplications] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const companyName = localStorage.getItem("companyName") || "Company";
  const userName = localStorage.getItem("userName") || "HR";

  /* ---------------- LOAD DASHBOARD ---------------- */

  useEffect(() => {

    const loadDashboard = async () => {

      try {

        const [dashRes, jobsRes] = await Promise.all([
          hrAPI.getDashboard(),
          hrAPI.getJobs()
        ]);

        setStats(dashRes.data || {});
        setJobs(jobsRes.data || []);

        if (jobsRes.data?.length > 0) {
          setSelectedJob(jobsRes.data[0]);
          fetchApplications(jobsRes.data[0].id, "all");
        }

      } catch {

        toast({
          title: "Session expired",
          description: "Please login again",
          variant: "destructive"
        });

        navigate("/hr/login");
      }

      setLoading(false);

    };

    loadDashboard();

  }, [navigate]);

  /* ---------------- FETCH APPLICATIONS ---------------- */

  const fetchApplications = async (jobId, stage = "all") => {

    try {

      setLoadingApplications(true);
      setSelectedStage(stage);

      const apiStage = stage === "all" ? null : stage;

      const res = await hrAPI.getJobApplications(jobId, apiStage);

      setApplications(res.data || []);

    } catch {

      setApplications([]);

    } finally {

      setLoadingApplications(false);

    }

  };

  /* ---------------- SEARCH FILTER ---------------- */

  const filteredApps = useMemo(() => {

    return applications.filter((app) => {

      const name =
        app.candidateDetails?.fullName ||
        app.applicantName ||
        "";

      return name.toLowerCase().includes(searchQuery.toLowerCase());

    });

  }, [applications, searchQuery]);

  /* ---------------- LOADING SCREEN ---------------- */

  if (loading) {

    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="animate-spin text-purple-600 w-10 h-10"/>
      </div>
    );

  }

  return (

    <div className="h-screen flex flex-col bg-gray-50">

      {/* HEADER */}

      <header className="bg-white border-b px-8 py-4 flex justify-between">

        <div className="flex items-center gap-3">

          <Users className="text-purple-600"/>

          <div>

            <h1 className="font-bold text-lg">
              HR Dashboard
            </h1>

            <p className="text-xs text-gray-500">
              {companyName} • {userName}
            </p>

          </div>

        </div>

        <div className="flex gap-4 items-center">

          <div className="relative">

            <Search className="absolute left-2 top-2 text-gray-400" size={16}/>

            <Input
              placeholder="Search candidate"
              className="pl-8 w-64"
              value={searchQuery}
              onChange={(e)=>setSearchQuery(e.target.value)}
            />

          </div>

          <Button
            variant="ghost"
            onClick={()=>{
              localStorage.clear();
              navigate("/hr/login");
            }}
          >
            <LogOut size={18}/>
          </Button>

        </div>

      </header>

      {/* PIPELINE CARDS */}

      <div className="px-8 pt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">

        {[
          {label:"Applications",count:stats.totalApplications,stage:"all"},
          {label:"Shortlisted",count:stats.shortlisted,stage:"shortlisted"},
          {label:"Schedule",count:stats.scheduled,stage:"scheduled"},
          {label:"Interview",count:stats.interviewed,stage:"interviewed"},
          {label:"Feedback",count:stats.feedback,stage:"feedback"},
          {label:"Offer",count:stats.offer,stage:"offer"},
          {label:"Hired",count:stats.hired,stage:"hired"},
          {label:"Rejected",count:stats.rejected,stage:"rejected"}
        ].map(card=>(
          <Card
            key={card.label}
            onClick={()=>selectedJob && fetchApplications(selectedJob.id,card.stage)}
            className={`p-4 cursor-pointer text-center ${
              selectedStage===card.stage ? "bg-purple-600 text-white":"bg-white"
            }`}
          >
            <p className="text-xs">{card.label}</p>
            <p className="text-lg font-bold">{card.count || 0}</p>
          </Card>
        ))}

      </div>

      {/* MAIN SECTION */}

      <main className="flex flex-1 overflow-hidden p-6 gap-6">

        {/* JOB LIST */}

        <Card className="w-64 overflow-y-auto">

          {jobs.map(job=>(
            <div
              key={job.id}
              onClick={()=>{
                setSelectedJob(job);
                fetchApplications(job.id, selectedStage);
              }}
              className={`p-4 border-b cursor-pointer ${
                selectedJob?.id===job.id
                  ? "bg-purple-50 border-l-4 border-purple-600"
                  : ""
              }`}
            >
              <h3 className="font-semibold text-sm">{job.title}</h3>

              <p className="text-xs text-gray-500">
                {job.applicationCount || 0} Candidates
              </p>

            </div>
          ))}

        </Card>

        {/* CANDIDATE TABLE */}

        <Card className="flex-1 overflow-y-auto">

          {loadingApplications ? (

            <div className="flex items-center justify-center p-20">
              <Loader2 className="animate-spin text-purple-600"/>
            </div>

          ) : (

            <table className="w-full text-left">

              <thead className="bg-gray-100 text-xs uppercase">

                <tr>
                  <th className="px-6 py-3">Candidate</th>
                  <th className="px-6 py-3">Phone</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3 text-right">Action</th>
                </tr>

              </thead>

              <tbody>

                {filteredApps.map((app)=>{

                  const name =
                    app.candidateDetails?.fullName ||
                    app.applicantName;

                  const phone =
                    app.candidateDetails?.phone ||
                    app.applicantPhone ||
                    "N/A";

                  return (

                    <tr key={app.id} className="border-t">

                      <td className="px-6 py-4 font-medium">
                        {name}
                      </td>

                      <td className="px-6 py-4 text-gray-500">
                        {phone}
                      </td>

                      <td className="px-6 py-4 text-xs">
                        {app.currentStage}
                      </td>

                      <td className="px-6 py-4 text-right">

                        <Button
                          size="sm"
                          onClick={()=>navigate(`/hr/candidate/${app.id}`)}
                        >
                          View Profile
                        </Button>

                      </td>

                    </tr>

                  );

                })}

              </tbody>

            </table>

          )}

        </Card>

      </main>

    </div>

  );

};

export default HRDashboard;